import React from "react";
import { ExternalLink, Headphones } from "lucide-react";
import { WORKOUT_TYPE_MAP } from "../../lib/coaching";
import { RichTextView, looksLikeHtml, decodeEntities } from "../RichTextEditor";

// Minimal in-line autolinker: returns an array of strings + <a> elements.
// Built with explicit function calls (no fragments, no recursion) so the
// CRA/Babel toolchain has no excuse to choke.
function linkify(text) {
  if (!text) return [];
  const out = [];
  const url = new RegExp(
    "(https?://[^\\s]+|www\\.[^\\s]+|[a-z0-9][a-z0-9-]*\\.(?:com|app|io|run|co|net|org|fit|me|tv|ca|us|uk)(?:/[^\\s]*)?)",
    "gi"
  );
  let last = 0;
  const matches = String(text).matchAll(url);
  let i = 0;
  for (const m of matches) {
    const start = m.index;
    if (start > last) out.push(text.slice(last, start));
    let raw = m[0];
    let trail = "";
    while (raw && ".,;:!?)".indexOf(raw[raw.length - 1]) >= 0) {
      trail = raw[raw.length - 1] + trail;
      raw = raw.slice(0, -1);
    }
    const href = /^https?:\/\//i.test(raw) ? raw : "https://" + raw;
    out.push(
      React.createElement(
        "a",
        {
          key: "ln-" + (i++),
          href: href,
          target: "_blank",
          rel: "noopener noreferrer",
          onClick: (e) => e.stopPropagation(),
          className:
            "text-[var(--tir-burgundy)] underline font-semibold break-words",
        },
        raw
      )
    );
    if (trail) out.push(trail);
    last = start + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function ZoneChip({ zones }) {
  if (!zones || zones.length === 0) return null;
  return (
    <span className="inline-block text-[9px] font-mono-tir bg-[var(--tir-ivory)] border border-[var(--tir-border)] text-[var(--tir-ink)] px-1.5 py-0.5 mr-1">
      Z{zones.join("·")}
    </span>
  );
}

function RpeChip({ rpeMin, rpeMax }) {
  if (rpeMin == null && rpeMax == null) return null;
  const label =
    rpeMin === rpeMax
      ? String(rpeMin)
      : (rpeMin == null ? "?" : rpeMin) + "–" + (rpeMax == null ? "?" : rpeMax);
  return (
    <span className="inline-block text-[9px] font-mono-tir bg-[var(--tir-ivory)] border border-[var(--tir-border)] text-[var(--tir-burgundy)] px-1.5 py-0.5 mr-1">
      RPE {label}
    </span>
  );
}

// Recursive segment renderer kept as a plain function (not a component) so
// React tree depth and Babel parse depth stay shallow.
function renderSegment(seg, depth) {
  depth = depth || 0;
  const padLeft = depth * 8;
  if (seg && seg.kind === "group") {
    const rounds = seg.repeat_count || 1;
    const inner = (seg.items || []).map((it, idx) =>
      <React.Fragment key={it.id || idx}>{renderSegment(it, depth + 1)}</React.Fragment>
    );
    return (
      <div style={{ paddingLeft: padLeft }} className="my-1" key={seg.id}>
        <div className="text-[10px] text-[var(--tir-ink)] font-semibold">
          <span className="text-[var(--tir-burgundy)]">×{rounds}</span>{" "}
          {(seg.label || "Block").trim()}
        </div>
        <div>{inner}</div>
      </div>
    );
  }
  const label = ((seg && seg.label) || "").trim();
  const valUnit = [seg && seg.value, seg && seg.unit].filter(Boolean).join(" ").trim();
  const pace = ((seg && seg.pace) || "").trim();
  const incline = ((seg && seg.incline) || "").toString().trim();
  const note = ((seg && seg.notes) || "").trim();
  return (
    <div
      key={seg && seg.id}
      style={{ paddingLeft: padLeft }}
      className="text-[10.5px] text-[var(--tir-ink)] my-0.5 leading-snug"
    >
      <ZoneChip zones={(seg && seg.zones) || []} />
      <RpeChip rpeMin={seg && seg.rpe_min} rpeMax={seg && seg.rpe_max} />
      {label ? <span className="font-semibold">{label}</span> : null}
      {valUnit ? (
        <span className="font-mono-tir text-[var(--tir-ink)]">
          {label ? " · " : ""}
          {valUnit}
        </span>
      ) : null}
      {pace ? (
        <span className="font-mono-tir text-[var(--tir-burgundy)]"> @ {pace}</span>
      ) : null}
      {incline ? (
        <span className="font-mono-tir text-[var(--tir-burgundy)] font-semibold"> · {incline}% incline</span>
      ) : null}
      {note ? (
        <div className="text-[10px] italic text-[var(--tir-muted)] mt-0.5">
          {linkify(note)}
        </div>
      ) : null}
    </div>
  );
}

function StrengthBlock({ block }) {
  if (!block) return null;
  const kindLabel = (block.kind || "block").toUpperCase();
  const items = block.exercises || [];
  return (
    <div className="my-1.5 border-t border-dashed border-[var(--tir-border)] pt-1.5">
      <div className="text-[9px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-ink)]">
        {kindLabel}
        {block.label ? <span className="text-[var(--tir-muted)]"> · {block.label}</span> : null}
        {block.repeat_count ? <span className="text-[var(--tir-burgundy)]"> ×{block.repeat_count}</span> : null}
        {block.cap_minutes ? <span className="text-[var(--tir-burgundy)]"> · {block.cap_minutes}min cap</span> : null}
      </div>
      {items.map(function (ex, i) {
        const meta = [
          ex.sets ? ex.sets + " sets" : null,
          ex.reps ? ex.reps + " reps" : null,
          ex.load ? "@ " + ex.load : null,
        ].filter(Boolean).join(" · ");
        return (
          <div
            key={i}
            className="text-[10.5px] text-[var(--tir-ink)] pl-2 border-l-2 border-[var(--tir-border)] my-0.5"
          >
            <span className="font-semibold">{ex.name || "Exercise"}</span>
            {meta ? <span className="font-mono-tir text-[var(--tir-muted)] ml-2">{meta}</span> : null}
            {ex.notes ? (
              <div className="text-[10px] italic text-[var(--tir-muted)]">
                {linkify(ex.notes)}
              </div>
            ) : null}
          </div>
        );
      })}
      {block.notes ? (
        <div className="text-[10px] italic text-[var(--tir-muted)] mt-1">
          {linkify(block.notes)}
        </div>
      ) : null}
    </div>
  );
}

export default function WorkoutDetail({ workout: wo }) {
  if (!wo) return null;
  const typeLabel = (WORKOUT_TYPE_MAP[wo.type] && WORKOUT_TYPE_MAP[wo.type].label) || wo.type;
  const segs = wo.segments || [];
  const blocks = wo.strength_blocks || [];
  const note = (wo.session_note || "").trim();
  const structure = (wo.structure || "").trim();
  const hasTopChip =
    (wo.zones && wo.zones.length) || wo.rpe_min != null || wo.rpe_max != null;

  return (
    <div className="space-y-1" data-testid={"athlete-workout-detail-" + wo.id}>
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-[9.5px] tracking-[0.18em] uppercase font-semibold text-[var(--tir-muted)]">
          {typeLabel}
        </div>
        {wo.duration_or_distance ? (
          <div className="font-mono-tir text-[11px] text-[var(--tir-burgundy)] font-semibold">
            {wo.duration_or_distance}
          </div>
        ) : null}
      </div>

      {hasTopChip ? (
        <div className="flex flex-wrap gap-1">
          <ZoneChip zones={wo.zones || []} />
          <RpeChip rpeMin={wo.rpe_min} rpeMax={wo.rpe_max} />
        </div>
      ) : null}

      {segs.length > 0 ? (
        <div className="border-t border-dashed border-[var(--tir-border)] pt-1 mt-1">
          {segs.map(function (s) { return renderSegment(s, 0); })}
        </div>
      ) : structure ? (
        <div className="text-[10.5px] text-[var(--tir-ink)] whitespace-pre-wrap leading-snug border-t border-dashed border-[var(--tir-border)] pt-1 mt-1">
          {linkify(structure)}
        </div>
      ) : null}

      {blocks.map(function (b, i) {
        return <StrengthBlock key={i} block={b} />;
      })}

      {wo.add_strides ? (
        <div className="text-[10px] font-semibold text-[var(--tir-burgundy)] mt-1">
          + {wo.strides_count || 4} {wo.strides_kind === "hill_surges" ? "hill surges" : "strides"} at the end
        </div>
      ) : null}

      {note ? (
        looksLikeHtml(note) ? (
          <RichTextView
            html={note}
            className="text-[10.5px] italic text-[var(--tir-ink)] bg-[var(--tir-ivory)] border-l-2 border-[var(--tir-navy)] px-2 py-1 mt-1.5 leading-snug"
          />
        ) : (
          <div className="text-[10.5px] italic text-[var(--tir-ink)] bg-[var(--tir-ivory)] border-l-2 border-[var(--tir-navy)] px-2 py-1 mt-1.5 whitespace-pre-wrap leading-snug">
            {linkify(decodeEntities(note))}
          </div>
        )
      ) : null}

      {wo.external_link || wo.audio_link ? (
        <div className="flex flex-wrap gap-2 mt-1.5">
          {wo.external_link ? (
            <a
              href={wo.external_link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={function (e) { e.stopPropagation(); }}
              data-testid={"athlete-workout-link-" + wo.id}
              className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-[var(--tir-burgundy)] hover:text-[var(--tir-burgundy-dark)] font-semibold border border-[var(--tir-burgundy)]/40 hover:border-[var(--tir-burgundy)] px-2 py-1"
            >
              <ExternalLink size={10} /> Workout
            </a>
          ) : null}
          {wo.audio_link ? (
            <a
              href={wo.audio_link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={function (e) { e.stopPropagation(); }}
              data-testid={"athlete-audio-link-" + wo.id}
              className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-[var(--tir-navy)] hover:text-[var(--tir-navy)] font-semibold border border-[var(--tir-navy)]/40 hover:border-[var(--tir-navy)] px-2 py-1"
            >
              <Headphones size={10} /> Audio
            </a>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
